import { flatMap, isEmpty } from 'lodash-es';
import type { ProductsState, ProductState } from '@aviata/products-fns';
import type { PassengersAncillaryState } from '@aviata/ancillary-fns';

import type { SelectedSeatsState } from '@/widgets/seat-map';

import type { productsTypes } from '@/entities/products';

export const extractPackage = (
  packageInfo: productsTypes.ExtendedPackageInfo | null,
  products: ProductsState | null,
) => {
  if (!packageInfo?.id || !products) {
    return null;
  }

  return {
    type: packageInfo.type || null,
    products: Object.keys(products).filter(productName => {
      const nextProduct = products[
        productName as keyof typeof products
      ] as ProductState;
      return nextProduct.selectedAsPackage;
    }),
  };
};

export const extractProducts = (
  products: ProductsState | null,
  hasMedicalInsurance: boolean,
  hasRoaming: boolean,
) => {
  const productsNames: string[] = [];

  if (hasMedicalInsurance) {
    productsNames.push('medical_insurance');
  }
  if (hasRoaming) {
    productsNames.push('roaming');
  }

  if (!products) {
    return productsNames.length > 0 ? productsNames : null;
  }

  Object.keys(products)
    .filter(productName => {
      // Skip roaming and medical_insurance if they were already added above
      if (hasRoaming && productName === 'roaming') return false;
      if (hasMedicalInsurance && productName === 'medical_insurance')
        return false;

      const nextProduct = products[
        productName as keyof typeof products
      ] as ProductState;
      return nextProduct.id && !nextProduct.selectedAsPackage;
    })
    .forEach(productName => productsNames.push(productName));

  return productsNames;
};

export const extractSeatsCodes = (selectedSeats: SelectedSeatsState | null) => {
  if (!selectedSeats) {
    return null;
  }
  return flatMap(selectedSeats, state => {
    if (state) {
      return Object.values(state)
        .map(value => value.seatKey)
        .filter(seat => seat.length > 0);
    }
  });
};

export const extractBaggageCount = (
  baggage: Partial<PassengersAncillaryState>,
) => {
  if (isEmpty(baggage)) {
    return null;
  }
  return flatMap(baggage, passengerState => {
    if (passengerState) {
      return Object.values(passengerState).map(
        selectedBaggage => selectedBaggage.id,
      );
    }
  }).length;
};

export const extractSeatsCount = (seatMap: SelectedSeatsState | null) => {
  if (!seatMap) {
    return 0;
  }

  return seatMap.reduce((previousCount, nextFlight) => {
    return (previousCount += Object.values(nextFlight).filter(
      seat => seat.seatKey,
    ).length);
  }, 0);
};
